import type { LayerSpecification, Map as MapLibreMap } from 'maplibre-gl'
import { BASEMAP_FONT, type BasemapMode } from './basemap'

// Reporting points over the airspace: IFR points from reporting-points.geojson
// (db/generate/airspace), VRPs from the API, one layer per kind so each has its own toggle.
//
// The symbols are ICAO Annex 4's reporting point: a triangle, filled when
// compulsory, open when on request, as en-route charts and the Finnish
// VACs draw them. ENR 4.4 does not say which name-codes are compulsory
// (in free route airspace none are), so they are all open. The two kinds
// share the symbol and differ in colour: chart ink for the IFR points,
// magenta for VRPs. Drawn once as white SDF images, so their colour
// follows the theme.

export const REPORTING_POINTS_SOURCE = 'reporting-points'
export const REPORTING_POINTS_URL = '/map/reporting-points.geojson'
// The VRPs are aerodrome data (#81), served by the API rather than baked
// into the static file. Same-origin, so the session cookie goes with it.
export const VRP_SOURCE = 'aerodrome-vrps'
export const VRP_URL = '/api/plan/vrps'

export interface ReportingPointView {
  vrp: boolean
  ifr: boolean
}

// VRPs matter to every VFR flight; the IFR points are for the curious.
export const DEFAULT_REPORTING_POINT_VIEW: ReportingPointView = { vrp: true, ifr: false }

export const REPORTING_POINT_LAYERS: Record<keyof ReportingPointView, string> = {
  vrp: 'reporting-point-vrp',
  ifr: 'reporting-point-ifr',
}
export const REPORTING_POINT_LAYER_IDS = Object.values(REPORTING_POINT_LAYERS)

const ICONS = {
  'rp-triangle-filled': (c: CanvasRenderingContext2D) => triangle(c, true),
  'rp-triangle-open': (c: CanvasRenderingContext2D) => triangle(c, false),
} as const
type IconName = keyof typeof ICONS

const SIZE = 32 // px, drawn at pixel ratio 2: a 16 px symbol on the map

function triangle(c: CanvasRenderingContext2D, filled: boolean) {
  c.beginPath()
  c.moveTo(SIZE / 2, 4)
  c.lineTo(SIZE - 4, SIZE - 6)
  c.lineTo(4, SIZE - 6)
  c.closePath()
  if (filled) c.fill()
  c.lineWidth = 4
  c.lineJoin = 'miter'
  c.stroke()
}

/**
 * Adds a reporting-point symbol the style asks for. Hooked to
 * `styleimagemissing`, so the images come back by themselves after a theme
 * switch replaces the style.
 */
export function addReportingPointImage(map: MapLibreMap, id: string) {
  if (!(id in ICONS) || map.hasImage(id)) return
  const canvas = document.createElement('canvas')
  canvas.width = SIZE
  canvas.height = SIZE
  const c = canvas.getContext('2d')
  if (!c) return
  c.fillStyle = '#ffffff'
  c.strokeStyle = '#ffffff'
  ICONS[id as IconName](c)
  map.addImage(id, c.getImageData(0, 0, SIZE, SIZE), { sdf: true, pixelRatio: 2 })
}

const TOKENS: Record<BasemapMode, { vrp: string; ifr: string; halo: string }> = {
  light: { vrp: '#ad1457', ifr: '#263238', halo: '#f3f3f1' },
  dark: { vrp: '#f48fb1', ifr: '#cfd8dc', halo: '#1b1b1f' },
}

const visibility = (on: boolean): 'visible' | 'none' => (on ? 'visible' : 'none')

export function reportingPointLayers(mode: BasemapMode, view: ReportingPointView): LayerSpecification[] {
  const t = TOKENS[mode]
  const common = {
    'icon-allow-overlap': true,
    'text-font': BASEMAP_FONT,
    'text-size': 10,
    'text-anchor': 'left' as const,
    'text-offset': [0.8, 0] as [number, number],
    'text-optional': true,
  }
  return [
    {
      id: REPORTING_POINT_LAYERS.ifr,
      type: 'symbol',
      source: REPORTING_POINTS_SOURCE,
      filter: ['==', ['get', 'kind'], 'ifr'],
      minzoom: 6,
      layout: {
        ...common,
        visibility: visibility(view.ifr),
        'icon-image': 'rp-triangle-open',
        'icon-size': ['interpolate', ['linear'], ['zoom'], 6, 0.6, 10, 0.9],
        'text-field': ['step', ['zoom'], '', 8, ['get', 'code']],
      },
      paint: { 'icon-color': t.ifr, 'text-color': t.ifr, 'text-halo-color': t.halo, 'text-halo-width': 1.2 },
    },
    {
      id: REPORTING_POINT_LAYERS.vrp,
      type: 'symbol',
      source: VRP_SOURCE,
      filter: ['==', ['get', 'kind'], 'vrp'],
      minzoom: 7,
      layout: {
        ...common,
        visibility: visibility(view.vrp),
        'icon-image': ['case', ['get', 'compulsory'], 'rp-triangle-filled', 'rp-triangle-open'],
        'icon-size': ['interpolate', ['linear'], ['zoom'], 7, 0.7, 11, 1],
        'text-field': ['step', ['zoom'], '', 8, ['coalesce', ['get', 'name'], ['get', 'code']]],
      },
      paint: { 'icon-color': t.vrp, 'text-color': t.vrp, 'text-halo-color': t.halo, 'text-halo-width': 1.2 },
    },
  ]
}
